/**
 * 과열증기 보정계수 KSH 조회.
 *
 * 원본 데이터: /data/api520-ksh.json
 *   API 520 Part I 10판(2020) Table 13 (SI 단위) — 원 출처 ASME 2017 BPVC Section I.
 *   PDF 글자 좌표로 기계 추출했고, 표준서 5.7.2 예제(0.855)를 0.8551 로 재현해 검증했다.
 *   데이터 수정 시 `python data/verify-api520-ksh.py` 재실행 필수.
 *
 * 표의 성격상 주의할 점:
 *   - 비어 있는 (압력, 온도) 조합은 그 압력에서 해당 온도가 과열 영역이 아니라는 뜻이다.
 *     **외삽하지 않는다.** 조회 불가를 그대로 반환해 설계자가 조건을 다시 보게 한다.
 *   - 표준서 원문이 13.75 MPa 열을 누락하고 있어 압력 축은 산술 수열이 아니다.
 */
import raw from "../../data/api520-ksh.json";

const PRESSURES: readonly number[] = raw.pressuresMPa;
const TEMPERATURES: readonly number[] = raw.temperaturesC;

/** rows[온도][압력] = KSH */
const ROWS: ReadonlyMap<number, ReadonlyMap<number, number>> = new Map(
  Object.entries(raw.rows).map(([t, cells]) => [
    Number(t),
    new Map(Object.entries(cells).map(([p, v]) => [Number(p), v as number])),
  ]),
);

export const KSH_PRESSURE_RANGE_MPA: readonly [number, number] = [
  PRESSURES[0]!,
  PRESSURES[PRESSURES.length - 1]!,
];

export const KSH_TEMPERATURE_RANGE_C: readonly [number, number] = [
  TEMPERATURES[0]!,
  TEMPERATURES[TEMPERATURES.length - 1]!,
];

/** 1200 °F. 이 온도를 넘으면 KSH 를 쓰지 말고 기체 사이징식을 쓴다(5.7.1). */
export const KSH_MAX_TEMPERATURE_C = 649;

export type KshLookup =
  | { readonly ok: true; readonly ksh: number; readonly interpolated: boolean }
  | {
      readonly ok: false;
      readonly reason: "out-of-range" | "not-tabulated";
      readonly message: string;
    };

function bracket(axis: readonly number[], v: number): [number, number] | null {
  if (v < axis[0]! || v > axis[axis.length - 1]!) return null;
  let lo = axis[0]!;
  let hi = axis[axis.length - 1]!;
  for (const a of axis) {
    if (a <= v && a > lo) lo = a;
    if (a >= v && a < hi) hi = a;
  }
  if (v <= axis[0]!) lo = axis[0]!;
  if (v >= axis[axis.length - 1]!) hi = axis[axis.length - 1]!;
  return [lo, hi];
}

/**
 * 과열증기 보정계수 KSH 를 조회한다(압력·온도 양방향 선형보간).
 *
 * @param pressureMPa    배출 압력 P1 (MPa 절대)
 * @param temperatureC   배출 온도 (℃)
 */
export function lookupKsh(
  pressureMPa: number,
  temperatureC: number,
): KshLookup {
  if (!Number.isFinite(pressureMPa) || !Number.isFinite(temperatureC)) {
    return {
      ok: false,
      reason: "out-of-range",
      message: "압력과 온도는 유한한 값이어야 합니다.",
    };
  }

  const pb = bracket(PRESSURES, pressureMPa);
  const tb = bracket(TEMPERATURES, temperatureC);
  if (!pb || !tb) {
    return {
      ok: false,
      reason: "out-of-range",
      message:
        `KSH 표의 범위를 벗어났습니다. 압력 ${KSH_PRESSURE_RANGE_MPA[0]}~${KSH_PRESSURE_RANGE_MPA[1]} MPa, ` +
        `온도 ${KSH_TEMPERATURE_RANGE_C[0]}~${KSH_TEMPERATURE_RANGE_C[1]} ℃ 범위에서만 조회할 수 있습니다.`,
    };
  }

  const [p0, p1] = pb;
  const [t0, t1] = tb;

  const cell = (p: number, t: number): number | undefined =>
    ROWS.get(t)?.get(p);

  const q00 = cell(p0, t0);
  const q10 = cell(p1, t0);
  const q01 = cell(p0, t1);
  const q11 = cell(p1, t1);

  if (
    q00 === undefined ||
    q10 === undefined ||
    q01 === undefined ||
    q11 === undefined
  ) {
    return {
      ok: false,
      reason: "not-tabulated",
      message:
        `압력 ${pressureMPa} MPa · 온도 ${temperatureC} ℃ 조합은 표에 값이 없습니다. ` +
        "해당 압력에서 그 온도는 과열 영역이 아닐 수 있습니다. 포화 여부를 확인하십시오.",
    };
  }

  const fp = p1 === p0 ? 0 : (pressureMPa - p0) / (p1 - p0);
  const ft = t1 === t0 ? 0 : (temperatureC - t0) / (t1 - t0);

  const a = q00 * (1 - fp) + q10 * fp;
  const b = q01 * (1 - fp) + q11 * fp;

  return {
    ok: true,
    ksh: a * (1 - ft) + b * ft,
    interpolated: fp !== 0 || ft !== 0,
  };
}
